import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Link } from "react-router-dom";
import { Sparkles, Compass, MessageCircle, Zap, Clock, Download, Share2 } from "lucide-react";
import { Button } from "../components/ui/button";
import PricingCTA from "../components/PricingCTA";
import ShareCardModal from "../components/ShareCardModal";
import JourneysSection from "../components/JourneysSection";
import CosmicWeatherAlerts from "../components/CosmicWeatherAlerts";
import DigestSettings from "../components/DigestSettings";
import WelcomeGifts from "../components/WelcomeGifts";
import BlueprintBanner from "../components/BlueprintBanner";
import ClaimAccountNudge from "../components/ClaimAccountNudge";
import ReferralsPanel from "../components/ReferralsPanel";
import { toast } from "sonner";
import { chartSignature } from "../lib/chartSignature";
import { recentScanClass } from "../lib/scoreTone";
import { log } from "../lib/log";

const ZODIAC_GLYPH = {
  Aries: "♈\uFE0E", Taurus: "♉\uFE0E", Gemini: "♊\uFE0E", Cancer: "♋\uFE0E", Leo: "♌\uFE0E", Virgo: "♍\uFE0E",
  Libra: "♎\uFE0E", Scorpio: "♏\uFE0E", Sagittarius: "♐\uFE0E", Capricorn: "♑\uFE0E", Aquarius: "♒\uFE0E", Pisces: "♓\uFE0E",
};

function useCountdown(seconds) {
  const [left, setLeft] = useState(seconds);
  useEffect(() => setLeft(seconds), [seconds]);
  useEffect(() => {
    const t = setInterval(() => setLeft((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, []);
  return left;
}

function fmt(s) {
  const h = String(Math.floor(s / 3600)).padStart(2, "0");
  const m = String(Math.floor((s % 3600) / 60)).padStart(2, "0");
  const sec = String(s % 60).padStart(2, "0");
  return `${h}:${m}:${sec}`;
}

export default function Dashboard() {
  const { user, refresh } = useAuth();
  const [data, setData] = useState({ capsules: [], seconds_to_reset: 0 });
  const [scans, setScans] = useState([]);
  const [shareOpen, setShareOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [c, s] = await Promise.all([api.get("/dashboard/capsules"), api.get("/scans")]);
        setData(c.data);
        setScans(s.data);
      } catch (e) {
        log.warn("Dashboard: capsules + scans fetch failed:", e);
      }
    })();
  }, []);

  // Stripe return handler: poll payment status when arriving with ?checkout=success&session_id=...
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const flag = params.get("checkout");
    const sessionId = params.get("session_id");
    if (!flag) return;

    const clean = () => {
      const url = new URL(window.location.href);
      url.searchParams.delete("checkout");
      url.searchParams.delete("session_id");
      window.history.replaceState({}, "", url.pathname + url.search);
    };

    if (flag === "cancel") {
      toast.error("Checkout cancelled");
      clean();
      return;
    }
    if (flag !== "success" || !sessionId) return;

    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 8;
    const t = toast.loading("Confirming payment with Stripe…");
    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const res = await api.get(`/payments/status/${sessionId}`);
        const p = res.data?.payment_status;
        if (p === "paid") {
          toast.success("Payment confirmed ✨ Access unlocked", { id: t });
          await refresh?.();
          clean();
          return;
        }
        if (res.data?.status === "expired" || p === "unpaid") {
          toast.error("Payment did not complete", { id: t });
          clean();
          return;
        }
      } catch (e) {
        // Transient network / 4xx while polling — keep trying until timeout.
        log.debug("Stripe poll transient error:", e?.response?.status || e?.message);
      }
      if (attempts >= maxAttempts) {
        toast.error("Payment status timed out. Check your email for confirmation.", { id: t });
        clean();
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [refresh]);

  const left = useCountdown(data.seconds_to_reset);
  const zodiac = user?.natal?.zodiac;
  const scansLeft = user?.vip_lifetime || user?.tier !== "free"
    ? "∞"
    : Math.max(0, 3 - (user?.daily_scan_count || 0));


  return (
    <div>
      <header className="mt-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-[#A89C98]">Welcome back</p>
          <h1 className="font-heading text-4xl sm:text-5xl tracking-tighter mt-2" data-testid="dashboard-greeting">
            {user?.name || "Traveler"} <span className="text-[#C8A96E]">{zodiac ? ZODIAC_GLYPH[zodiac] : ""}</span>
          </h1>
          {zodiac && (
            <p className="text-[#A89C98] mt-2">
              Sun in <span className="text-white">{zodiac}</span>
              {user?.natal?.chinese_zodiac && (
                <>
                  {" · "}
                  <span className="text-white">Year of the {user.natal.chinese_zodiac}</span>
                </>
              )}
              {" · "}
              <span className="text-[#C8A96E]" data-testid="dashboard-personalized-tag">
                {user?.natal?.personalized_tagline || user?.natal?.personalized_tag || "Path calibrated"}
              </span>
            </p>
          )}
          {(() => {
            const sig = chartSignature(user?.natal);
            if (!sig) return null;
            return (
              <button
                type="button"
                data-testid="chart-signature-chip"
                onClick={() => {
                  navigator.clipboard?.writeText(sig).then(
                    () => toast.success(`${sig} copied — your chart signature`),
                    () => toast.error("Copy failed")
                  );
                }}
                title="Your unique chart signature · click to copy"
                className="mt-3 inline-flex items-center gap-2 rounded-full border border-[#C8A96E]/50 bg-[#C8A96E]/5 px-3 py-1.5 hover:bg-[#C8A96E]/15 transition-colors group"
              >
                <span className="text-[9px] uppercase tracking-[0.28em] text-[#A89C98] group-hover:text-white">
                  Chart Signature
                </span>
                <span className="font-mono-scanner text-[13px] text-[#C8A96E]" data-testid="chart-signature-value">
                  {sig}
                </span>
              </button>
            );
          })()}
        </div>
        <Link to="/scanner" className="w-full md:w-auto">
          <Button data-testid="dashboard-scan-cta" className="w-full md:w-auto bg-[#C8A96E] text-[#15111A] hover:bg-[#A98B55] rounded-none px-14 h-20 sm:h-[90px] text-xl sm:text-2xl font-heading font-bold uppercase tracking-widest shadow-[0_0_45px_rgba(200,169,110,0.4)] hover:shadow-[0_0_70px_rgba(200,169,110,0.55)] hover:-translate-y-0.5 transition-all">
            Enter Scanner →
          </Button>
        </Link>
      </header>

      {/* "1 scan remaining today" nudge — free tier only, when 2 scans used (1 of 3 left) */}
      {user?.tier === "free" && !user?.vip_lifetime && (user?.daily_scan_count || 0) === 2 && (
        <div
          className="mt-6 flex items-center gap-3 rounded-full border border-[#C8A96E] bg-[#C8A96E]/10 px-4 py-2 w-fit animate-pulse-slow"
          data-testid="dashboard-scans-remaining-badge"
        >
          <Clock size={14} className="text-[#C8A96E]" />
          <span className="text-[11px] uppercase tracking-[0.28em] text-[#C8A96E] font-bold">
            1 scan remaining today
          </span>
        </div>
      )}

      {/* Share Postcard bar — only when user has at least one scan */}
      {scans.length > 0 && (
        <div className="mt-6 flex items-center justify-between gap-4 rounded-xl border border-[#C8A96E]/30 bg-white/[0.02] px-5 py-3" data-testid="dashboard-share-bar">
          <div className="flex items-center gap-3 min-w-0">
            <Share2 size={16} className="text-[#C8A96E] shrink-0" />
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#A89C98]">Blueprint Postcard</div>
              <div className="text-sm text-white/90 truncate">Turn your top scan into a shareable card</div>
            </div>
          </div>
          <Button
            data-testid="dashboard-share-postcard-btn"
            onClick={() => setShareOpen(true)}
            className="bg-transparent border border-[#C8A96E] text-[#C8A96E] hover:bg-[#C8A96E] hover:text-[#15111A] rounded-none h-9 px-4 uppercase tracking-widest text-[11px] font-bold"
          >
            Share Postcard
          </Button>
        </div>
      )}

      {/* Prominent Unlock + PDF row — always visible, but hides individual CTAs if owned */}
      <section className="mt-10" data-testid="dashboard-monetization">
        <ClaimAccountNudge />
        <BlueprintBanner className="mb-5" testid="dashboard-blueprint-banner" />
        {user?.is_owner && <ReferralsPanel />}

        {/* Pricing tier CTAs */}
        <PricingCTA />
      </section>

      {/* Scarcity wheel */}
      <section className="mt-10 grid md:grid-cols-3 gap-6" data-testid="scarcity-wheel">
        {data.capsules.map((c, i) => (
          <div key={c.title} className="glass rounded-2xl p-6 relative overflow-hidden hover:-translate-y-1 transition-transform">
            <div className="flex items-center justify-between">
              <span className="text-xs uppercase tracking-[0.2em] text-[#C8A96E]">Capsule {i + 1}</span>
              <Sparkles size={16} className="text-[#C8A96E]" />
            </div>
            <h3 className="font-heading text-2xl mt-4">{c.title}</h3>
            <p className="text-sm text-[#A89C98] mt-3 leading-relaxed">{c.hint}</p>
            <div className="mt-6 flex items-center gap-2 text-xs text-[#A89C98]">
              <Clock size={12} />
              <span className="font-mono-scanner" data-testid={`capsule-timer-${i}`}>{fmt(left)}</span>
              <span>until reset</span>
            </div>
          </div>
        ))}
      </section>

      {/* Stats */}
      <section className="mt-10 grid md:grid-cols-3 gap-6">
        <div className="glass rounded-2xl p-6" data-testid="stat-scans-left">
          <div className="text-xs uppercase tracking-[0.2em] text-[#A89C98]">Scans Left Today</div>
          <div className="font-mono-scanner text-5xl mt-3 text-[#C8A96E]">{scansLeft}</div>
        </div>
        <div className="glass rounded-2xl p-6" data-testid="stat-tier">
          <div className="text-xs uppercase tracking-[0.2em] text-[#A89C98]">Access Tier</div>
          <div className="font-heading text-3xl mt-3 capitalize">{user?.vip_lifetime ? "Infinity" : (user?.tier || "Free")}</div>
        </div>
        <div className="glass rounded-2xl p-6" data-testid="stat-total-scans">
          <div className="text-xs uppercase tracking-[0.2em] text-[#A89C98]">Total Scans</div>
          <div className="font-mono-scanner text-5xl mt-3 text-white">{scans.length}</div>
        </div>
      </section>

      {/* Recent scans */}
      <section className="mt-10">
        <h2 className="font-heading text-2xl tracking-tight mb-4">Recent Trajectories</h2>
        {scans.length === 0 ? (
          <div className="glass rounded-2xl p-8 text-center text-[#A89C98]" data-testid="empty-scans">
            No scans yet. The scanner awaits your first coordinate.
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4" data-testid="recent-scans-list">
            {scans.slice(0, 6).map((s) => (
              <div key={s.scan_id} className="glass rounded-xl p-5 flex items-center justify-between">
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-[#A89C98]">{new Date(s.created_at).toLocaleString()}</div>
                  <div className="font-heading text-xl mt-1">{s.city}</div>
                </div>
                <div className={`font-mono-scanner text-3xl ${recentScanClass(s)}`}>
                  {s.score}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Welcome PDFs — 8-Day Healing Blueprint + Destination Wellness Ch.1 */}
      <WelcomeGifts />

      {/* Weekly cosmic weather — rare transits touching saved cities */}
      <CosmicWeatherAlerts />

      {/* Weekly digest opt-in/out + preview */}
      <DigestSettings />

      {/* Your Journeys — saved wellness trip plans */}
      <JourneysSection />

      <ShareCardModal open={shareOpen} onClose={() => setShareOpen(false)} />
    </div>
  );
}
